import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { FaDownload, FaLink, FaCheck, FaRedo, FaLock } from 'react-icons/fa';
import { incrementDownloadCount, getDownloadCount, getRatingSummary } from './firebase';
import RatingWidget from './RatingWidget';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const PLATFORMS = [
  { name: 'TikTok',      note: 'No watermark', ok: true },
  { name: 'X / Twitter', note: 'Public posts',  ok: true },
  { name: 'Reddit',      note: 'Public videos', ok: true },
  { name: 'Instagram',   note: 'Public Reels',  ok: true },
  { name: 'YouTube',     note: 'Not supported', ok: false },
];

const FORMAT_OPTIONS = [
  { formatType: 'audio_video', quality: 'best', label: 'HD Video',   desc: 'Best quality · MP4' },
  { formatType: 'audio_video', quality: '720p', label: '720p Video', desc: 'Smaller file · MP4'  },
  { formatType: 'audio_video', quality: '480p', label: '480p Video', desc: 'Mobile size · MP4'   },
  { formatType: 'audio_only',  quality: 'best', label: 'Audio Only', desc: '192 kbps · MP3'      },
];

const FAQS = [
  {
    q: 'Which platforms are supported?',
    a: 'TikTok (no watermark), X/Twitter, Instagram Reels (public), and Reddit videos. YouTube is not supported.',
  },
  {
    q: 'How do I download a TikTok without watermark?',
    a: 'Paste the TikTok video URL, select HD Video, and click Download. The watermark is automatically removed.',
  },
  {
    q: 'Is it free?',
    a: 'Yes — 100% free. No sign-up, no account, no limits.',
  },
  {
    q: 'What is the difference between HD, 720p, and 480p?',
    a: 'HD downloads the original quality from the source. 720p and 480p are compressed versions — smaller file size, faster download, ideal for mobile storage.',
  },
  {
    q: 'Why is YouTube not supported?',
    a: "YouTube's platform restrictions prevent server-side downloads. Use your browser's built-in download feature instead.",
  },
  {
    q: 'How do I download an Instagram Reel?',
    a: 'Open the Instagram Reel in your browser, copy the URL from the address bar, paste it into DownloadMediaHub, and click Download. Only public Reels can be downloaded.',
  },
  {
    q: 'How do I download a Reddit video?',
    a: 'Copy the Reddit post URL (not the video URL directly), paste it into DownloadMediaHub, and click Download. Reddit videos with separate audio tracks are automatically merged.',
  },
  {
    q: 'Can I download audio only as MP3?',
    a: 'Yes — select the "Audio Only" option before clicking Download. The audio is extracted and saved as a 192 kbps MP3 file.',
  },
  {
    q: 'Is it safe to use?',
    a: 'Yes. We do not store your videos or URLs. Files are processed server-side and deleted immediately after download. No personal data is collected.',
  },
  {
    q: 'Do I need to install anything?',
    a: 'No. DownloadMediaHub works entirely in your browser — no app, no extension, no software to install.',
  },
  {
    q: 'What video formats are downloaded?',
    a: 'Videos are downloaded as MP4 files, which are compatible with all devices and video editors. Audio is downloaded as MP3.',
  },
];

const PLATFORM_GUIDES = [
  {
    platform: 'TikTok',
    icon: '🎵',
    steps: [
      'Open TikTok and find the video you want to save.',
      'Tap Share → Copy Link.',
      'Paste the link into DownloadMediaHub.',
      'Select HD Video and click Download.',
      'The video saves without the TikTok watermark.',
    ],
  },
  {
    platform: 'X / Twitter',
    icon: '🐦',
    steps: [
      'Open the X post containing the video.',
      'Click the share icon → Copy link to post.',
      'Paste into DownloadMediaHub and click Download.',
      'Works for public posts only.',
    ],
  },
  {
    platform: 'Instagram',
    icon: '📸',
    steps: [
      'Open the Reel in your browser (not the app).',
      'Copy the URL from the address bar.',
      'Paste into DownloadMediaHub and click Download.',
      'Only public Reels can be downloaded.',
    ],
  },
  {
    platform: 'Reddit',
    icon: '🤖',
    steps: [
      'Open the Reddit post with the video.',
      'Copy the post URL from your browser.',
      'Paste into DownloadMediaHub and click Download.',
      'Audio and video are automatically merged.',
    ],
  },
];


function ContactForm() {
  const [email,   setEmail]   = useState('');
  const [message, setMessage] = useState('');
  const [status,  setStatus]  = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim() || !message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(`${API_BASE}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient: 'admin@aivantage.tools',
          subject:   'General Enquiry',
          body:      `From: ${email}\n\n${message}`,
          reply_to:  email,
          site_name: 'DownloadMediaHub',
        }),
      });
      setStatus(res.ok ? 'sent' : 'error');
    } catch {
      setStatus('error');
    }
  }

  return (
    <section className="dmh-how" aria-label="Contact us">
      <h2 className="dmh-section-title">Contact Us</h2>
      <p style={{ fontSize: '0.88rem', color: '#475569', marginBottom: 16 }}>Have a question or feedback? We read every message.</p>
      {status === 'sent' ? (
        <p style={{ color: '#16a34a', fontWeight: 600 }}>✅ Message sent — we'll get back to you soon.</p>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Your email" required
            style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.9rem' }}
          />
          <textarea
            value={message} onChange={e => setMessage(e.target.value)}
            placeholder="Your question or feedback…" rows={4} required
            style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.9rem', resize: 'vertical', fontFamily: 'inherit' }}
          />
          {status === 'error' && <p style={{ color: '#dc2626', fontSize: '0.85rem' }}>Something went wrong. Please try again.</p>}
          <button className="dmh-btn" type="submit" disabled={status === 'sending'} style={{ width: 'fit-content' }}>
            {status === 'sending' ? 'Sending…' : 'Send Message'}
          </button>
        </form>
      )}
    </section>
  );
}

export default function App() {
  const [url,         setUrl]         = useState('');
  const [selected,    setSelected]    = useState(0);
  const [consent,     setConsent]     = useState(false);
  const [isLoading,   setIsLoading]   = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState(false);
  const [dlCount,     setDlCount]     = useState(null);
  const [ratingSummary, setRatingSummary] = useState(null);

  useEffect(() => {
    getDownloadCount().then(setDlCount);
    getRatingSummary().then(setRatingSummary);
  }, []);

  const isYouTube = /youtube\.com|youtu\.be/i.test(url);
  const opt = FORMAT_OPTIONS[selected];

  const handleDownload = async () => {
    if (!consent)    { setError('Please accept the terms first.'); return; }
    if (!url.trim()) { setError('Please paste a video URL.'); return; }
    if (isYouTube)   { setError('YouTube is not supported. Try TikTok, X, Instagram, or Reddit.'); return; }

    setIsLoading(true);
    setError('');
    setSuccess(false);

    const fd = new FormData();
    fd.append('url',         url.trim());
    fd.append('format_type', opt.formatType);
    fd.append('quality',     opt.quality);
    fd.append('consent',     'true');

    try {
      const res = await axios.post(`${API_BASE}/public/media-download`, fd, {
        responseType: 'blob',
        timeout: 120000,
      });

      const ext       = opt.formatType === 'audio_only' ? 'mp3' : 'mp4';
      const objectUrl = window.URL.createObjectURL(res.data);
      const a         = document.createElement('a');
      a.href          = objectUrl;
      a.download      = `download.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
      setSuccess(true);
      incrementDownloadCount();
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text().catch(() => '');
        try { setError(JSON.parse(text).error || 'Download failed. Please try again.'); }
        catch { setError('Download failed. Please try again.'); }
      } else {
        setError(err.response?.data?.error || err.message || 'Download failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setUrl('');
    setSelected(0);
    setConsent(false);
    setError('');
    setSuccess(false);
  };

  return (
    <div className="dmh-app">
      <Helmet>
        <title>TikTok & X Video Downloader — No Watermark, Free | DownloadMediaHub</title>
        <meta name="description" content="Download TikTok videos without watermark, X/Twitter videos, Instagram Reels & Reddit videos free. No sign-up. Save as MP4 or MP3. Works on iPhone, Android & desktop." />
        <link rel="canonical" href="https://www.downloadmediahub.com/" />
      </Helmet>

      {/* ── Nav ────────────────────────────────────────────────────── */}
      <nav className="dmh-nav">
        <span className="dmh-logo">⬇ DownloadMediaHub</span>
        <span className="dmh-nav-tag">
          <span className="dmh-nav-badge dmh-nav-badge--green">100% Free</span>
          <span className="dmh-nav-badge dmh-nav-badge--blue">No Sign-Up</span>
          <span className="dmh-nav-badge dmh-nav-badge--purple">Instant</span>
        </span>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────────── */}
      <header className="dmh-hero">
        <h1>TikTok & X Video Downloader — No Watermark, Free</h1>
        <p className="dmh-subtitle">
          TikTok No Watermark · X/Twitter Video Downloader · Instagram Reel Downloader · Reddit Video Downloader
        </p>
      </header>

      {/* ── Main ───────────────────────────────────────────────────── */}
      <main className="dmh-main">

        {error   && <div className="dmh-alert dmh-alert--error" role="alert">{error}</div>}
        {success && (
          <div className="dmh-alert dmh-alert--success" role="status">
            <FaCheck /> Download started — check your browser downloads.
          </div>
        )}

        {/* Loading — prime ad spot */}
        {isLoading ? (
          <div className="dmh-loading-card">
            <div className="dmh-spinner" />
            <p className="dmh-loading-text">Fetching your video… please wait</p>
          </div>
        ) : (
          <div className="dmh-card">
            {/* URL input */}
            <div className="dmh-input-row">
              <FaLink className="dmh-input-icon" />
              <input
                type="url"
                className="dmh-input"
                placeholder="Paste TikTok, X, Instagram or Reddit URL…"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setError(''); setSuccess(false); }}
                aria-label="Video URL"
              />
            </div>
            {isYouTube && <p className="dmh-hint dmh-hint--warn">YouTube is not supported.</p>}

            {/* Quality / format selector */}
            <div className="dmh-formats" role="radiogroup" aria-label="Select quality">
              {FORMAT_OPTIONS.map((o, i) => (
                <label key={i} className={`dmh-format ${selected === i ? 'dmh-format--active' : ''}`}>
                  <input
                    type="radio"
                    name="format"
                    checked={selected === i}
                    onChange={() => setSelected(i)}
                  />
                  <span className="dmh-format-label">{o.label}</span>
                  <span className="dmh-format-desc">{o.desc}</span>
                </label>
              ))}
            </div>

            {/* Consent */}
            <label className="dmh-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>
                I confirm I have the right to download this content and accept full legal responsibility.{' '}
                <a href="/terms" className="dmh-link">Terms</a>
              </span>
            </label>

            {/* Download button */}
            <button className="dmh-btn" onClick={handleDownload} disabled={!url.trim() || !consent || isLoading}>
              <FaDownload /> Download
            </button>

            {(url || success) && (
              <button className="dmh-btn-ghost" onClick={handleReset}>
                <FaRedo /> Reset
              </button>
            )}
          </div>
        )}

        {/* Rating widget — shown after successful download */}
        {success && <RatingWidget />}

        {/* ── About intro ──────────────────────────────────────────── */}
        <section className="dmh-how" aria-label="About DownloadMediaHub">
          <h2 className="dmh-section-title">Free Online Video Downloader — TikTok, X, Instagram & Reddit</h2>
          <p style={{ fontSize: '0.9rem', color: '#334155', lineHeight: 1.8, marginTop: 12 }}>
            Welcome to <strong>DownloadMediaHub</strong> — your all-in-one free video downloader for social media. Whether you want to save a TikTok video without the watermark, download an X/Twitter video in HD, grab an Instagram Reel, or save a Reddit video with audio, DownloadMediaHub handles it all in seconds. No app to install, no account to create, completely free.
          </p>
          <p style={{ fontSize: '0.9rem', color: '#334155', lineHeight: 1.8, marginTop: 10 }}>
            Unlike other downloaders that only support one platform, DownloadMediaHub works across <strong>TikTok, X/Twitter, Instagram, and Reddit</strong>. You can also extract audio and save it as an MP3 file — perfect for saving podcast clips, music videos, or speech content for offline listening. All downloads are processed securely and files are deleted from our servers immediately after download, ensuring your privacy.
          </p>
        </section>

        {/* ── Platform support ─────────────────────────────────────── */}
        <section className="dmh-platforms" aria-label="Supported platforms">
          <h2 className="dmh-section-title">Supported Platforms</h2>
          <div className="dmh-platform-grid">
            {PLATFORMS.map((p) => (
              <div key={p.name} className={`dmh-platform-badge ${p.ok ? '' : 'dmh-platform-badge--no'}`}>
                <span className="dmh-platform-icon">{p.ok ? '✅' : '❌'}</span>
                <span className="dmh-platform-name">{p.name}</span>
                <span className="dmh-platform-note">{p.note}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── How it works ─────────────────────────────────────────── */}
        <section className="dmh-how" aria-label="How it works">
          <h2 className="dmh-section-title">How It Works</h2>
          <ol className="dmh-steps">
            <li><strong>Paste</strong> the video URL from TikTok, X, Instagram, or Reddit.</li>
            <li><strong>Choose</strong> your quality — HD, 720p, 480p, or Audio MP3.</li>
            <li><strong>Accept</strong> the copyright terms.</li>
            <li><strong>Click Download</strong> — the file saves straight to your device.</li>
          </ol>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────── */}
        <section className="dmh-faq" aria-label="Frequently asked questions">
          <h2 className="dmh-section-title">Frequently Asked Questions</h2>
          {FAQS.map((f, i) => (
            <details key={i} className="dmh-faq-item">
              <summary className="dmh-faq-q">{f.q}</summary>
              <p className="dmh-faq-a">{f.a}</p>
            </details>
          ))}
        </section>

        {/* ── Benefits ─────────────────────────────────────────────── */}
        <section className="dmh-how" aria-label="Benefits">
          <h2 className="dmh-section-title">Why Choose DownloadMediaHub?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 16 }}>
            {[
              { icon: '🚫', title: 'No Watermark', desc: 'TikTok videos download completely clean — no TikTok logo or watermark on your saved video.' },
              { icon: '🎵', title: 'Audio MP3 Download', desc: 'Extract audio from any video and save it as a high-quality 192 kbps MP3 file. Unique to DownloadMediaHub.' },
              { icon: '📱', title: '4 Platforms in One', desc: 'TikTok, X/Twitter, Instagram Reels, and Reddit — all supported in one place. No need for separate tools.' },
              { icon: '🔒', title: 'Privacy First', desc: 'We never store your videos or URLs. All files are deleted from our servers immediately after download.' },
              { icon: '⚡', title: 'Fast Downloads', desc: 'Our servers process your download in seconds. No waiting, no queue, no delays.' },
              { icon: '🖥️', title: 'No Install Needed', desc: 'Works entirely in your browser on any device — desktop, iPhone, or Android. No app or extension required.' },
            ].map((b) => (
              <div key={b.title} style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 16px' }}>
                <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>{b.icon}</div>
                <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{b.title}</h3>
                <p style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Device guides ─────────────────────────────────────────── */}
        <section className="dmh-how" aria-label="Device download guides">
          <h2 className="dmh-section-title">How to Download Videos on Any Device</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 16 }}>
            <div style={{ background: '#f0f9ff', borderRadius: 12, border: '1px solid #bae6fd', padding: '18px 16px' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>📱 On iPhone (iOS)</h3>
              <ol style={{ paddingLeft: 18, fontSize: '0.85rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: 8, lineHeight: 1.6 }}>
                <li>Open Safari and go to <strong>downloadmediahub.com</strong>.</li>
                <li>Copy the video URL from TikTok, X, Instagram, or Reddit.</li>
                <li>Paste the URL and select your quality.</li>
                <li>Click Download — Safari will prompt you to save the file.</li>
                <li>Find your video in the <strong>Files app</strong> → Downloads folder.</li>
              </ol>
            </div>
            <div style={{ background: '#f0fdf4', borderRadius: 12, border: '1px solid #86efac', padding: '18px 16px' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>🤖 On Android</h3>
              <ol style={{ paddingLeft: 18, fontSize: '0.85rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: 8, lineHeight: 1.6 }}>
                <li>Open Chrome and go to <strong>downloadmediahub.com</strong>.</li>
                <li>Copy the video URL from any supported app.</li>
                <li>Paste the URL, choose quality, and click Download.</li>
                <li>The video downloads directly to your <strong>Downloads folder</strong>.</li>
                <li>Open your Gallery or Files app to find the saved video.</li>
              </ol>
            </div>
            <div style={{ background: '#faf5ff', borderRadius: 12, border: '1px solid #d8b4fe', padding: '18px 16px' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>💻 On Desktop (PC/Mac)</h3>
              <ol style={{ paddingLeft: 18, fontSize: '0.85rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: 8, lineHeight: 1.6 }}>
                <li>Go to <strong>downloadmediahub.com</strong> in any browser.</li>
                <li>Copy the video URL from TikTok, X, Instagram, or Reddit.</li>
                <li>Paste the URL into the input box.</li>
                <li>Select your preferred quality and click Download.</li>
                <li>The video saves to your <strong>Downloads folder</strong> automatically.</li>
              </ol>
            </div>
          </div>
        </section>

        {/* ── Features ──────────────────────────────────────────────── */}
        <section className="dmh-how" aria-label="Features">
          <h2 className="dmh-section-title">Features of DownloadMediaHub</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 16 }}>
            {[
              { title: 'TikTok Downloader Without Watermark', desc: 'Download any public TikTok video in original HD quality with the watermark completely removed. Perfect for saving trending content, repurposing videos, or archiving your own posts.' },
              { title: 'X / Twitter Video Downloader', desc: 'Save videos from X (formerly Twitter) posts in HD, 720p, or 480p. Works with public tweets. Copy the tweet URL and paste it — download starts instantly.' },
              { title: 'Instagram Reel Downloader', desc: 'Download public Instagram Reels as MP4 files. No login required. Simply copy the Reel URL from your browser and paste it into DownloadMediaHub.' },
              { title: 'Reddit Video Downloader with Audio', desc: 'Reddit videos often have separate audio and video streams. DownloadMediaHub automatically merges them into a single MP4 file so you get the complete video with sound.' },
              { title: 'Audio MP3 Extractor', desc: 'Select "Audio Only" to extract just the audio from any supported video. Saved as a 192 kbps MP3 — ideal for music, podcasts, speeches, or language learning.' },
              { title: 'Multiple Quality Options', desc: 'Choose from HD (original quality), 720p (balanced), 480p (mobile-friendly), or Audio Only. Pick the right format for your storage and usage needs.' },
            ].map((f) => (
              <div key={f.title} style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 16px' }}>
                <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0ea5e9', marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: '0.83rem', color: '#334155', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Platform Guides ──────────────────────────────────────── */}
        <section className="dmh-how" aria-label="Platform download guides">
          <h2 className="dmh-section-title">How to Download from Each Platform</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 16 }}>
            {PLATFORM_GUIDES.map((g) => (
              <div key={g.platform} style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
                  {g.icon} {g.platform}
                </h3>
                <ol style={{ paddingLeft: 18, fontSize: '0.82rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {g.steps.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </div>
            ))}
          </div>
        </section>

        {/* ── Contact form ─────────────────────────────────────────── */}
        <ContactForm />
      </main>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="dmh-footer">
        {(dlCount || ratingSummary) && (
          <div className="dmh-stats">
            {dlCount && <span>⬇ {dlCount.toLocaleString()} videos downloaded</span>}
            {dlCount && ratingSummary && <span className="dmh-stats-sep">·</span>}
            {ratingSummary && <span>⭐ {ratingSummary.avg} from {ratingSummary.count} users</span>}
          </div>
        )}
        <div className="dmh-footer-links">
          <a href="/about" className="dmh-link">About</a>
          <span>·</span>
          <a href="/blog" className="dmh-link">Blog</a>
          <span>·</span>
          <a href="/terms" className="dmh-link">Terms of Use</a>
          <span>·</span>
          <a href="/privacy" className="dmh-link">Privacy Policy</a>
        </div>
        <p className="dmh-footer-copy">
          © {new Date().getFullYear()} DownloadMediaHub. For personal use only.
        </p>
        <p className="dmh-footer-disclaimer">
          <FaLock style={{ fontSize: '0.65rem', marginRight: 3 }} />
          We do not store your videos. Files are deleted immediately after download.
        </p>
      </footer>
    </div>
  );
}
